import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { Icon } from '../../design-system/icons';
import { readDisplayPreference, writeDisplayPreference } from '../../views/collection/display-preference';
import type { CanvasNode, InspectorView, LibraryEntry, NodeActions } from './canvas-types';
import type { EditorCommand } from './shortcuts';

/**
 * Panneaux de la coquille des studios : bibliothèque (recherche, touche « / »), calques synchronisés avec le
 * canevas (clavier : flèches, Entrée), inspecteur à onglets et barre de sélection, plus les petites pièces du
 * chrome (bouton à icône avec infobulle et raccourci, aide ⓘ qui s'ouvre sur demande). Génériques : ils ne
 * connaissent que des nœuds, des entrées et des onglets fournis par le profil. Une explication ne s'affiche
 * jamais en permanence : elle s'ouvre au survol ou au clic, puis se referme (finition de l'éditeur, ADR-146).
 */


const normalize = (value: string): string => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase('fr');

/**
 * Bouton à icône du chrome : nom accessible, infobulle « Annuler · Ctrl Z », raccourci déclaré. Les actions
 * principales seules gardent un libellé ; tout le reste est un bouton à icône de 32 px.
 */
export function IconButton({ icon, label, keys, keysInName = true, shortcut, onClick, disabled, pressed, className, describedBy, size = 16, children }: {
  icon: string;
  label: string;
  /** Raccourci en mots (« Ctrl Z »), montré dans l'infobulle. */
  keys?: string | undefined;
  /** Le raccourci fait partie du nom accessible (« Annuler · Ctrl Z ») ; sinon il n'est que dans l'infobulle. */
  keysInName?: boolean | undefined;
  /** Raccourci au format `aria-keyshortcuts` (« Control+Z »). */
  shortcut?: string | undefined;
  onClick?: (() => void) | undefined;
  disabled?: boolean | undefined;
  pressed?: boolean | undefined;
  className?: string | undefined;
  describedBy?: string | undefined;
  size?: number | undefined;
  children?: ReactNode;
}) {
  const tip = keys === undefined ? label : `${label} · ${keys}`;
  return (
    <button
      type="button"
      className={`ay-studio__icon${className === undefined ? '' : ` ${className}`}`}
      aria-label={keysInName ? tip : label}
      title={tip}
      disabled={disabled}
      aria-pressed={pressed}
      aria-keyshortcuts={shortcut}
      aria-describedby={describedBy}
      onClick={onClick}
    >
      <Icon name={icon} size={size} />
      {children}
    </button>
  );
}

/**
 * Bulle qui s'ouvre sur demande (aide ⓘ, couche, avertissement) : un déclencheur, un contenu non modal.
 * Échap ou un clic ailleurs la referme ; Échap ne remonte pas jusqu'à l'éditeur (il ne le ferme pas).
 */
export function Popover({ trigger, label, children, className, align = 'start' }: {
  /** Déclencheur ; son nom accessible est à sa charge. */
  trigger: (props: { open: boolean; toggle: () => void; id: string }) => ReactNode;
  label: string;
  children: ReactNode;
  className?: string;
  align?: 'start' | 'end' | 'center';
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const root = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (event: MouseEvent): void => {
      if (root.current !== null && !root.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);
  const onKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key !== 'Escape' || !open) return;
    event.stopPropagation();
    event.preventDefault();
    setOpen(false);
  };
  return (
    <span ref={root} className={`ay-studio-pop${className === undefined ? '' : ` ${className}`}`} onKeyDown={onKeyDown}>
      {trigger({ open, toggle: () => setOpen((value) => !value), id })}
      {open && (
        <span id={id} role="dialog" aria-label={label} className={`ay-studio-pop__panel is-${align}`}>
          {children}
        </span>
      )}
    </span>
  );
}

/** Aide ⓘ : une icône discrète ; l'explication s'ouvre au clic et se lit au survol (infobulle). */
export function InfoTip({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Popover
      label={label}
      className="ay-studio-info"
      trigger={({ open, toggle, id }) => (
        <button type="button" className="ay-studio-info__button" aria-label={label} title={typeof children === 'string' ? children : label} aria-expanded={open} aria-controls={open ? id : undefined} onClick={toggle}>
          <Icon name="info" size={14} />
        </button>
      )}
    >
      {children}
    </Popover>
  );
}

/**
 * Aide de premier usage : montrée tant qu'elle n'a pas servi. Préférence du poste, gardée par le module
 * isolé des préférences d'affichage (aucune autre écriture du navigateur ici) ; sans stockage, elle revient.
 */
export function useFirstUseHint(key: string): [boolean, () => void] {
  const preference = `studio-hint.${key}`;
  const [shown, setShown] = useState(() => readDisplayPreference(preference, ['seen']) === undefined);
  const dismiss = (): void => {
    if (!shown) return;
    setShown(false);
    writeDisplayPreference(preference, 'seen');
  };
  return [shown, dismiss];
}

/** En-tête d'un panneau du rail : titre, aide éventuelle, fermeture. */
export function PanelHead({ title, help, onClose }: { title: string; help?: ReactNode; onClose?: () => void }) {
  return (
    <header className="ay-studio-panel__head">
      <strong className="ay-studio-panel__title">{title}</strong>
      {help !== undefined && <InfoTip label={`Aide : ${title}`}>{help}</InfoTip>}
      <span className="ay-studio__spacer" />
      {onClose !== undefined && <IconButton icon="close" label={`Fermer le panneau ${title}`} onClick={onClose} className="ay-studio__icon--sm" size={14} />}
    </header>
  );
}

/** Pastille d'origine d'une entrée : seulement pour un Pack, un client ou une solution, jamais pour le Core. */
function originPill<D>(entry: LibraryEntry<D>): string | undefined {
  if (entry.origin === undefined || entry.layer === undefined || entry.layer === 'core') return undefined;
  return entry.origin.replace(/^(?:sous-)?pack\s+/i, '');
}

/** Infobulle d'une entrée : nom complet, ce qu'elle fait, sa mention. */
function entryTip<D>(entry: LibraryEntry<D>): string {
  // L'origine seulement pour un Pack ou un client : « Core » n'apprend rien à l'auteur.
  return [entry.label, entry.hint ?? entry.meta, entry.note, entry.unsupported, entry.layer === 'core' ? undefined : entry.origin].filter((part, index, all): part is string => part !== undefined && part !== '' && all.indexOf(part) === index).join(' · ');
}

/**
 * Bibliothèque (éditeur v2, planche « Panneaux-Widgets ») : la recherche en tête, les onglets en segment
 * d'icônes à infobulle, des catégories repliables, des tuiles à trois colonnes (icône, un mot ; nom complet et
 * description en infobulle), des lignes pour les préréglages. Une pastille seulement pour un Pack ou un client.
 * Le panneau tient dans sa hauteur : seule la liste défile.
 */
export function LibraryPanel<D>({ entries, groups, groupIcons, searchRef, target, refusal, onInsert, onClearTarget }: {
  entries: readonly LibraryEntry<D>[];
  groups: readonly string[];
  /** Icône par onglet : segment d'icônes ; sinon, des onglets en texte. */
  groupIcons?: Readonly<Record<string, string>> | undefined;
  searchRef: RefObject<HTMLInputElement | null>;
  /** Point d'insertion en mots (« après Chasseur »), ou `null` : après la sélection. */
  target: string | null;
  refusal: string | null;
  onInsert: (entry: LibraryEntry<D>) => void;
  onClearTarget: () => void;
}) {
  const [group, setGroup] = useState(groups[0] ?? '');
  const [search, setSearch] = useState('');
  const [folded, setFolded] = useState<ReadonlySet<string>>(() => new Set());
  const [pinnedOpen, setPinnedOpen] = useState(true);
  const query = normalize(search.trim());
  const pinned = entries.filter((entry) => entry.pinned === true);
  // La recherche porte sur le nom, le mot de la tuile, la description, la catégorie et l'origine (« Gibier »).
  const shown = query === ''
    ? entries.filter((entry) => entry.pinned !== true && entry.group === group)
    : entries.filter((entry) => entry.pinned !== true && normalize(`${entry.label} ${entry.word ?? ''} ${entry.meta ?? ''} ${entry.hint ?? ''} ${entry.section ?? ''} ${entry.origin ?? ''}`).includes(query));
  // Catégories dans l'ordre d'apparition ; en recherche, une par onglet.
  const sections: Array<{ name: string; items: LibraryEntry<D>[] }> = [];
  for (const entry of shown) {
    const name = query === '' ? (entry.section ?? '') : entry.group;
    const found = sections.find((candidate) => candidate.name === name);
    if (found === undefined) sections.push({ name, items: [entry] });
    else found.items.push(entry);
  }
  const toggle = (key: string): void => setFolded((current) => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });
  const glyph = (entry: LibraryEntry<D>, size: number) => (entry.icon !== undefined ? <Icon name={entry.icon} size={size} /> : <span className="ay-studio__glyph">{entry.glyph ?? '•'}</span>);
  const tile = (entry: LibraryEntry<D>) => {
    const pill = originPill(entry);
    return (
      <li key={entry.id}>
        <button
          type="button"
          data-library-entry={entry.id}
          className={`ay-studio-tile${entry.unsupported !== undefined ? ' is-muted' : ''}${entry.note !== undefined ? ' is-placed' : ''}`}
          onClick={() => onInsert(entry)}
          title={entryTip(entry)}
          aria-label={entry.note === undefined ? entry.label : `${entry.label}, ${entry.note}`}
          aria-describedby={entry.unsupported === undefined ? undefined : `${entry.id}-unsupported`}
        >
          <span className="ay-studio-tile__icon" aria-hidden="true">{glyph(entry, 20)}</span>
          <span className="ay-studio-tile__word" aria-hidden="true">{entry.word ?? entry.label}</span>
          {pill !== undefined && <span className={`ay-studio-tile__pill is-${entry.layer}`} aria-hidden="true">{pill}</span>}
          {entry.note !== undefined && <span className="ay-studio-tile__note" aria-hidden="true"><Icon name="check" size={10} /></span>}
          {entry.unsupported !== undefined && <span id={`${entry.id}-unsupported`} className="ay-visually-hidden">{entry.unsupported}</span>}
        </button>
      </li>
    );
  };
  const row = (entry: LibraryEntry<D>) => {
    const pill = originPill(entry);
    return (
      <li key={entry.id}>
        <button
          type="button"
          data-library-entry={entry.id}
          className={`ay-studio-row${entry.unsupported !== undefined ? ' is-muted' : ''}`}
          onClick={() => onInsert(entry)}
          title={entryTip(entry)}
          aria-describedby={entry.unsupported === undefined ? undefined : `${entry.id}-unsupported`}
        >
          <span className="ay-studio-row__icon" aria-hidden="true">{glyph(entry, 14)}</span>
          <span className="ay-studio-row__label">{entry.label}</span>
          {entry.note !== undefined && <span className="ay-studio-row__note" aria-hidden="true"><Icon name="check" size={12} /></span>}
          {entry.note !== undefined && <span className="ay-visually-hidden">, {entry.note}</span>}
          {pill !== undefined && <span className={`ay-studio-tile__pill is-${entry.layer}`}>{pill}</span>}
          {entry.unsupported !== undefined && <span id={`${entry.id}-unsupported`} className="ay-visually-hidden">{entry.unsupported}</span>}
        </button>
      </li>
    );
  };
  const list = (items: LibraryEntry<D>[]) => {
    const rows = items.filter((entry) => entry.row === true || entry.preview !== undefined);
    const tiles = items.filter((entry) => entry.row !== true && entry.preview === undefined);
    return (
      <>
        {tiles.length > 0 && <ul className="ay-studio-tiles">{tiles.map(tile)}</ul>}
        {rows.length > 0 && <ul className="ay-studio-rows">{rows.map(row)}</ul>}
      </>
    );
  };
  return (
    <div className="ay-studio-library">
      <div className="ay-studio-library__head">
        <label className="ay-studio-search">
          <Icon name="search" size={14} />
          <span className="ay-visually-hidden">Rechercher un bloc ou un champ</span>
          <input ref={searchRef} type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Rechercher" />
          <kbd aria-hidden="true">/</kbd>
        </label>
        {query === '' && groups.length > 1 && (
          <div role="tablist" aria-label="Bibliothèque" className={groupIcons === undefined ? 'ay-studio-tabs' : 'ay-studio-library__seg'}>
            {groups.map((name) => {
              const icon = groupIcons?.[name];
              return (
                <button key={name} type="button" role="tab" aria-selected={group === name} onClick={() => setGroup(name)} {...(icon === undefined ? {} : { 'aria-label': name, title: name })}>
                  {icon === undefined ? name : <Icon name={icon} size={16} />}
                </button>
              );
            })}
          </div>
        )}
        {target !== null && (
          <p className="ay-studio-chip">
            <span>Insertion : {target}</span>
            <IconButton icon="close" label="Insérer après la sélection" onClick={onClearTarget} className="ay-studio__icon--sm" size={12} />
          </p>
        )}
        {refusal !== null && <p className="ay-studio__refusal" role="alert">{refusal}</p>}
      </div>
      <div className="ay-studio-library__list ay-studio__scroll">
        {pinned.length > 0 && (
          <div className="ay-studio-library__pinned">
            <button type="button" className="ay-studio-library__pinned-head" aria-expanded={pinnedOpen} onClick={() => setPinnedOpen((value) => !value)}>
              <Icon name="alert" size={12} />
              <span>À placer · {pinned.length}</span>
              <Icon name="chevron-down" size={12} />
            </button>
            {pinnedOpen && <ul className="ay-studio-rows" aria-label="À placer">{pinned.map(row)}</ul>}
          </div>
        )}
        {sections.map((section) => {
          const key = `${query === '' ? group : '?'}|${section.name}`;
          const open = !folded.has(key);
          return (
            <section key={section.name || 'all'} className="ay-studio-library__section" aria-label={section.name || (query === '' ? group : 'Résultats')}>
              {section.name !== '' && (
                <button type="button" className="ay-studio-library__category" aria-expanded={open} onClick={() => toggle(key)}>
                  <Icon name="chevron-down" size={12} />
                  <span className="ay-studio-library__category-name">{section.name}</span>
                  <span className="ay-studio-library__count">{section.items.length}</span>
                </button>
              )}
              {open && list(section.items)}
            </section>
          );
        })}
        {shown.length === 0 && <p className="ay-studio-empty">{query === '' ? 'Rien ici.' : 'Rien ne correspond.'}</p>}
      </div>
    </div>
  );
}

/** Légende des calques, ouverte sur demande depuis l'en-tête du panneau. */
export const LAYERS_HELP = '⚡ réagit · ❖ bloc de la bibliothèque. Alt ↑ et Alt ↓ montent et descendent l’élément choisi.';

/** Calques : l'arbre du document, synchronisé avec le canevas ; flèches haut et bas, Entrée pour choisir. */
export function LayersPanel({ root, selected, counts, onSelect, onAdd }: {
  root: CanvasNode;
  selected: string;
  counts: ReadonlyMap<string, { errors: number; warnings: number }>;
  onSelect: (id: string) => void;
  onAdd: (target: string) => void;
}) {
  const onKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const items = [...event.currentTarget.querySelectorAll<HTMLElement>('[data-layer]')];
    const index = items.indexOf(document.activeElement as HTMLElement);
    const next = items[Math.max(0, Math.min(items.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)))];
    if (next !== undefined) {
      event.preventDefault();
      next.focus();
    }
  };
  const row = (node: CanvasNode, depth: number) => {
    const count = counts.get(node.id);
    return (
      <li key={node.id} data-node={node.id} role="treeitem" aria-level={depth + 1} aria-selected={node.id === selected} className={`ay-studio-layer${node.heading === true ? ' is-heading' : ''}${node.id === selected ? ' is-selected' : ''}`}>
        <div className="ay-studio-layer__row" style={{ paddingInlineStart: `${4 + depth * 12}px` }}>
          {node.heading === true ? (
            <span className="ay-studio-layer__heading">{node.label}</span>
          ) : (
            <button type="button" data-layer="" className="ay-studio-layer__main" onClick={() => onSelect(node.id)} aria-current={node.id === selected ? 'true' : undefined}>
              {node.icon !== undefined ? <span aria-hidden="true" className="ay-studio__glyph ay-studio__glyph--icon"><Icon name={node.icon} size={14} /></span> : node.glyph !== undefined && <span aria-hidden="true" className="ay-studio__glyph">{node.glyph}</span>}
              <span className="ay-studio-layer__label">{node.label}</span>
              <span className="ay-visually-hidden">, {node.kind}</span>
              {node.conditional === true && (
                <span className="ay-studio-layer__conditional" title="Visible sous condition" data-testid="layer-conditional">
                  <Icon name="eye-off" size={12} />
                  <span className="ay-visually-hidden">, visible sous condition</span>
                </span>
              )}
              {(node.badges ?? []).map((badge) => <span key={badge} className="ay-studio-layer__badge" aria-hidden="true">{badge}</span>)}
              {node.muted !== undefined && <span className="ay-studio-layer__muted" title={node.muted}><Icon name="alert" size={12} /><span className="ay-visually-hidden">{node.muted}</span></span>}
              {count !== undefined && count.errors > 0 && <span className="ay-studio__count is-error">{count.errors}<span className="ay-visually-hidden"> à corriger</span></span>}
              {count !== undefined && count.errors === 0 && count.warnings > 0 && <span className="ay-studio__count is-warning">{count.warnings}<span className="ay-visually-hidden"> avertissement(s)</span></span>}
            </button>
          )}
          {node.insertTarget !== undefined && (
            <IconButton icon="plus" label={`Ajouter dans ${node.label}`} onClick={() => onAdd(node.insertTarget!)} className="ay-studio__icon--sm ay-studio-layer__add" size={14} />
          )}
        </div>
        {node.children.length > 0 && <ul role="group">{node.children.map((child) => row(child, depth + 1))}</ul>}
      </li>
    );
  };
  return (
    <nav className="ay-studio-layers ay-studio__scroll" aria-label="Calques" onKeyDown={onKeyDown}>
      <ul role="tree" aria-label="Éléments du document">{row(root, 0)}</ul>
    </nav>
  );
}

/**
 * Inspecteur (éditeur v2, planche « Panneaux-Widgets ») : un en-tête (icône, nom, sorte, action « Langues »),
 * puis les groupes du profil dans leur ordre, chacun repliable ; un groupe replié montre son résumé en un mot.
 * L'ouverture des groupes est gardée d'un élément à l'autre (comme Figma).
 */
export function InspectorPanel({ view, open, onToggle, chrome }: { view: InspectorView; open: Readonly<Record<string, boolean>>; onToggle: (id: string, open: boolean) => void; chrome?: Readonly<Record<string, string>> | undefined }) {
  const id = useId();
  return (
    <aside className="ay-studio__inspector" aria-label="Inspecteur" style={chrome}>
      <header className="ay-studio-inspector__head">
        {view.icon !== undefined && <span className="ay-studio-inspector__icon" aria-hidden="true"><Icon name={view.icon} size={16} /></span>}
        <span className="ay-studio-inspector__titles">
          <strong title={view.title}>{view.title}</strong>
          <span className="ay-studio__muted">{view.subtitle}</span>
        </span>
        {view.origin !== undefined && <span className="ay-studio-pill">{view.origin}</span>}
        {view.action}
      </header>
      <div className="ay-studio-inspector__body ay-studio__scroll">
        {view.tabs.length === 0 && <p className="ay-studio-empty">Rien à régler pour cet élément.</p>}
        {view.tabs.map((group) => {
          const shown = open[group.id] ?? group.open ?? true;
          const summary = group.summary ?? (group.count !== undefined && group.count > 0 ? String(group.count) : undefined);
          return (
            <section key={group.id} className={`ay-studio-group${shown ? ' is-open' : ''}`} data-group={group.id}>
              <button type="button" className="ay-studio-group__head" aria-expanded={shown} aria-controls={`${id}-${group.id}`} aria-label={group.label} aria-describedby={summary === undefined || shown ? undefined : `${id}-${group.id}-summary`} onClick={() => onToggle(group.id, !shown)}>
                <Icon name="chevron-down" size={14} />
                <span className="ay-studio-group__title">{group.label}</span>
                {!shown && summary !== undefined && <span id={`${id}-${group.id}-summary`} className={`ay-studio-group__summary is-${group.id}`}>{summary}</span>}
              </button>
              {shown && <div id={`${id}-${group.id}`} role="group" aria-label={group.label} className="ay-studio-group__body">{group.render()}</div>}
            </section>
          );
        })}
      </div>
    </aside>
  );
}

/**
 * Barre de l'élément choisi, flottante en haut du canevas : parent, déplacer vers une zone, monter, descendre,
 * dupliquer, supprimer, ajouter ici. Sans sélection, rien ne s'affiche (l'aide « cliquez un élément » est une
 * aide de premier usage, montrée par la coquille).
 */
export function SelectionBar<D>({ label, actions, chrome, onRun, onApply, onRefused, onAdd }: {
  label: string;
  actions: NodeActions<D>;
  chrome?: Readonly<Record<string, string>> | undefined;
  onRun: (command: EditorCommand) => void;
  onApply: (next: D) => void;
  onRefused: (message: string) => void;
  onAdd: () => void;
}) {
  const moveId = useId();
  if (label === '') return null;
  const tool = (icon: string, text: string, keys: string, command: EditorCommand, enabled: boolean, danger = false) => (
    <IconButton icon={icon} label={text} keys={keys} disabled={!enabled} onClick={() => onRun(command)} className={danger ? 'is-danger' : undefined} />
  );
  return (
    <div className="ay-studio-selection" role="toolbar" aria-label={`Élément choisi : ${label}`} style={chrome}>
      <strong className="ay-studio-selection__label" title={label}>{label}</strong>
      <span className="ay-studio-selection__sep" aria-hidden="true" />
      {tool('corner-up', 'Choisir le parent', 'Ctrl ↑', 'parent', actions.parent !== undefined)}
      {(actions.moveTo ?? []).length > 0 && (
        <label className="ay-studio-selection__move" htmlFor={moveId}>
          <span className="ay-visually-hidden">Déplacer vers une autre zone</span>
          <Icon name="move" size={14} />
          <select
            id={moveId}
            value=""
            title="Déplacer vers une autre zone"
            onChange={(event) => {
              const target = actions.moveTo?.[Number(event.target.value)];
              if (target === undefined) return;
              const result = target.apply();
              if (result !== null && typeof result === 'object' && 'refused' in (result as object)) onRefused((result as { refused: string }).refused);
              else onApply(result as D);
            }}
          >
            <option value="">Déplacer vers…</option>
            {(actions.moveTo ?? []).map((target, index) => <option key={target.label} value={index}>{target.label}</option>)}
          </select>
        </label>
      )}
      {tool('arrow-up', 'Monter', 'Alt ↑', 'move_up', actions.moveUp !== undefined)}
      {tool('arrow-down', 'Descendre', 'Alt ↓', 'move_down', actions.moveDown !== undefined)}
      {tool('copy', 'Dupliquer', 'Ctrl D', 'duplicate', actions.duplicate !== undefined)}
      {tool('trash', 'Supprimer', 'Suppr', 'remove', actions.remove !== undefined, true)}
      {actions.insertAfter !== undefined && (
        <>
          <span className="ay-studio-selection__sep" aria-hidden="true" />
          <button type="button" className="ay-studio__button ay-studio__button--accent" onClick={onAdd} title="Ajouter un élément juste après">
            <Icon name="plus" size={14} />
            Ajouter ici
          </button>
        </>
      )}
    </div>
  );
}
